import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { StoreState, SearchListing, MarketSlug, Market, Submarket } from '@root/types';
import api from '@shared/api';
import routes from '@root/routes';
import { getSearchListingIds } from '@store/selectors';
import listingSearchPageActions from '@store/actions/listingSearchPage';
import actions from '@store/actions';
import classnames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { OnlyInDesktopTablet, ConfirmationModal, OnlyInMobile } from '@components/shared';
import useBreakpoint, { breakpoints } from '@root/shared/useBreakpoint';
import useMarket from '@root/shared/useMarket';
import useEnv from '@root/shared/useEnv';
import SearchCriteriaBar from './SearchCriteriaBar';
import MobileSearchCriteriaBar from './SearchCriteriaBar/MobileSearchCriteriaBar';
import Layout from '../Layout';
import { useListingSearchCriteria, getSearchCriteriaPills } from './utils';
import s from './ListingSearch.module.less';
import Cards from './Cards';
import ListingSearchFiltersSidebar from './Filters/ListingSearchFiltersSidebar';
import Map from './Map';
import { FilterPillOptions } from './SearchCriteriaBar/FilterPill/FilterPill';
import PaginationSection from './PaginationSection';
import MarketSelector from './MarketSelector';
import { useFilterOptions } from './utils/FilterOptions/FilterOptionsProvider';
import NotFound from '../404/NotFound';
import useMapMovementCriteria from './useMapMovementCriteria';
// import SaveSearch from './SaveSearch/SaveSearch';
import SaveSearchSelector from './SaveSearchSelector';
import SaveSearchButton from './NamedSaveSearch/SaveSearchButton';

interface ReadonlySubmarketResults {
  readonly name: string;
  readonly slug: string;
}
interface ReadonlyListingSearchFiltersResults {
  submarkets: Array<ReadonlySubmarketResults>;
}

const getInitialListingSearchFilters = async (
  initialFilters,
): Promise<ReadonlyListingSearchFiltersResults> => {
  const response = await api.fetch(
    routes.api.listingSearchFilters({
      submarketSlugs: initialFilters.submarkets || [],
      landlordsIds: initialFilters.landlords || [],
      buildingIds: initialFilters.buildingIds || [],
    }),
  );
  return response.json();
};

const initialSearchFiltersExist = initialFilters => {
  return (
    initialFilters.submarkets?.length ||
    initialFilters.buildingIds?.length ||
    initialFilters.landlords?.length
  );
};

const mapState = (state: StoreState) => ({
  ids: getSearchListingIds(state),
  buildingSearchResults: state.buildings.searchResults,
  currentUser: state.currentUser,
  userMovedMap: state.map.moved,
  currentPage: state.listings.pagination?.currentPage,
});

const mapDispatch = {
  loadSearchResults: ({
    pageNumber,
    apiBuildingsUrl,
    apiListingsUrl,
  }: {
    pageNumber: number;
    apiBuildingsUrl: string;
    apiListingsUrl: string;
  }) =>
    listingSearchPageActions.loadSearchResults({
      pageNumber,
      apiBuildingsUrl,
      apiListingsUrl,
    }),
};

const DEFAULT_MARKET_SLUG = 'new_york_city';

const connector = connect(mapState, mapDispatch);
export type ReduxProps = ConnectedProps<typeof connector>;

export const RawListingSearch = ({
  buildingSearchResults,
  loadSearchResults,
  currentUser,
  userMovedMap,
  currentPage,
}: ReduxProps) => {
  const flags = useFlags();
  const dispatch = useDispatch();
  const breakpoint = useBreakpoint();
  const [sidebarIsCollapsed, setSidebarIsCollapsed] = useState(false);
  const toggleSidebar = useCallback(
    () => setSidebarIsCollapsed(!sidebarIsCollapsed),
    [sidebarIsCollapsed],
  );
  const [filtersList, setFiltersList] = useState<FilterPillOptions[]>([]);
  const [hoveredListing, setHoveredListing] = useState<SearchListing | null>(null);
  const [state, dispatchFilter] = useFilterOptions();
  const [should404, setShould404] = useState<boolean>(false);
  const { currentMarket, isOnCustomMarket } = useMarket();
  const {
    confirmMapMovement,
    exitMapMovementConfirmation,
    moveMap,
    onMapReset,
    onRedoSearch,
    onDrawComplete,
    invalidSearchModalOpen,
    closeInvalidSearchModal,
  } = useMapMovementCriteria();
  const [mostRecentNonCustomMarket, setMostRecentNonCustomMarket] = useState<Market | undefined>(
    currentMarket,
  );
  const { locale } = useEnv();
  const startingMarketSlug = currentMarket?.id ?? currentUser?.marketSlug ?? DEFAULT_MARKET_SLUG;
  const previousMarket = mostRecentNonCustomMarket?.id || currentUser?.marketSlug;
  const { t } = useTranslation('listingSearch');

  const criteria = useListingSearchCriteria({
    onUrlChange: c => {
      loadSearchResults({
        pageNumber: c.page(),
        apiBuildingsUrl: c.toApiBuildingsUrl(),
        apiListingsUrl: c.toApiListingsUrl(),
      });
      setFiltersList(
        getSearchCriteriaPills(c.currentFilters, state, { currentMarket, currentUser, locale }),
      );
      if (criteria.currentFilters.polygons) {
        dispatch(actions.setDrawnPolygons(c.parsedPolygons()));
      }
    },
  });

  /* Some of the filters like submarkets, buildings, and landlords don't have translations for their
     values. On page load the url/criteria only has access to the slugs/ids not the names. So this
     useEffect will query the backend on page load for all the initial listing search criteria
     filters so that the app has access to the filters respective names/labels.
   */
  useEffect(() => {
    if (initialSearchFiltersExist(criteria.currentFilters)) {
      getInitialListingSearchFilters(criteria.currentFilters).then(initialListingSearchFilters => {
        dispatchFilter({
          type: 'ADD_INITIAL_FILTER_OPTIONS',
          payload: initialListingSearchFilters,
        });
      });
    }
  }, []);

  useEffect(() => {
    setFiltersList(
      getSearchCriteriaPills(criteria.currentFilters, state, {
        currentMarket,
        currentUser,
        locale,
      }),
    );
  }, [state]);

  useEffect(() => {
    const container = document.getElementById('listing-cards-container');
    if (container?.scrollTo) {
      container.scrollTo(0, 0);
    }
  }, [currentPage]);

  const selectedSubmarkets: Submarket[] =
    criteria.currentFilters.submarkets
      ?.map(submarketSlug => state.submarkets[submarketSlug])
      .filter(submarket => submarket != null) || [];

  const removeAllFilters = () => {
    criteria.removeAll();
    criteria.changeMarket(previousMarket as MarketSlug);
    criteria.pushToHistory();
  };

  const removeFilters = filterKeys => {
    filterKeys.split(',').forEach(filterKey => {
      const [filter, value] = filterKey.split('|');
      if (value) {
        criteria.removeValue(filter, value);
      } else {
        criteria.remove(filter);
        if (filter === 'polygons') {
          criteria.removePolygons(previousMarket as MarketSlug);
        }
        if (filter === 'map') {
          criteria.removeMap(previousMarket as MarketSlug);
        }
      }
    });
    criteria.pushToHistory();
  };

  useEffect(() => {
    const isInvalidCriteria = !isOnCustomMarket && currentMarket?.id !== startingMarketSlug;
    if (should404 !== isInvalidCriteria) setShould404(isInvalidCriteria);
    if (currentMarket != null) setMostRecentNonCustomMarket(currentMarket);
  }, [currentMarket]);

  if (should404) return <NotFound />;

  const showTopSaveSearchButton =
    breakpoint === breakpoints.DESKTOP && flags['save-search-redesign'];

  // Will be updated after Eric's changes are merged and maybe logic
  // for SaveSearchSelector will be added to its own file?
  const options = [
    { value: 'exampleData', label: 'ExampleData' },
    { value: 'exampleData2', label: 'ExampleData2' },
  ];
  const value = { value: 'exampleData', label: 'ExampleData' };
  const toggleSaveSearch = () => {};

  return (
    <Layout
      key="search-layout"
      activePage="listingSearch"
      data-testid="listing-search-v2"
      showFooter={false}
      containerClassName={s.layout}
    >
      <section className={s.topSection}>
        <div className={s.topSectionTopRow}>
          <div className={s.listingSearchResultCount}>
            <MarketSelector />
          </div>
          {flags['save-search-redesign'] ? (
            <div>
              <SaveSearchSelector
                name="SaveSearchSelector"
                value={value}
                options={options}
                onChange={toggleSaveSearch}
              />
            </div>
          ) : null}
          {showTopSaveSearchButton && (
            <div className={s.saveSearch}>
              <SaveSearchButton disabled={filtersList.length < 1} criteria={criteria} />
            </div>
          )}
        </div>
        <div className={s.listingSearchResultCriteria}>
          {flags['mobile-search-filter'] ? (
            <>
              <OnlyInDesktopTablet>
                <SearchCriteriaBar
                  options={filtersList}
                  onFilterPillClick={removeFilters}
                  removeAll={removeAllFilters}
                />
              </OnlyInDesktopTablet>
              <OnlyInMobile preventRendering>
                <MobileSearchCriteriaBar
                  options={filtersList}
                  onFilterPillClick={removeFilters}
                  removeAll={removeAllFilters}
                />
              </OnlyInMobile>
            </>
          ) : (
            <SearchCriteriaBar
              options={filtersList}
              onFilterPillClick={removeFilters}
              removeAll={removeAllFilters}
            />
          )}
        </div>
      </section>
      <div className={s.listingSearchResultsContainer}>
        <OnlyInDesktopTablet>
          <div
            className={classnames(s.filterSidebarContainer, {
              [s['filterSidebarContainer--collapsed']]: sidebarIsCollapsed,
            })}
            data-testid="filter-sidebar-container"
          >
            <ListingSearchFiltersSidebar
              isCollapsed={sidebarIsCollapsed}
              toggleSidebar={toggleSidebar}
            />
          </div>
        </OnlyInDesktopTablet>
        <div
          className={classnames(s.listingResults, {
            [s['listingResults--withExpandedSidebar']]: !sidebarIsCollapsed,
          })}
        >
          <div id="listing-cards-container" className={s.listingCardsContainer}>
            <ul>
              <Cards searchCardHovered={setHoveredListing} />
            </ul>
          </div>
          <div className={s.paginationContainer}>
            <PaginationSection />
          </div>
        </div>
        <div
          className={classnames(s.mapContainer, {
            [s['mapContainer--withExpandedSidebar']]: !sidebarIsCollapsed,
          })}
        >
          <ConfirmationModal
            onConfirm={moveMap}
            onClose={exitMapMovementConfirmation}
            isOpen={confirmMapMovement}
            confirmationText={t('marketMoveConfirmationModal.bodyText')}
            confirmButtonText={t('marketMoveConfirmationModal.confirm')}
            cancelButtonText={t('common:cancel')}
            title={t('marketMoveConfirmationModal.title')}
          />
          <ConfirmationModal
            isOpen={invalidSearchModalOpen}
            onClose={closeInvalidSearchModal}
            title={t('invalidSearchModal.title')}
            confirmationText={t('invalidSearchModal.bodyText')}
            confirmButtonText={t('common:ok')}
            onConfirm={closeInvalidSearchModal}
            hideCancelButton
          />
          <Map
            buildingSearchResults={buildingSearchResults}
            customMapCoordinates={criteria.mapCoordinates()}
            selectedSubmarkets={selectedSubmarkets}
            hoveredListing={hoveredListing}
            userMovedMap={userMovedMap}
            marketSlug={(isOnCustomMarket && 'custom') || currentMarket?.id || startingMarketSlug}
            onRedoSearch={onRedoSearch}
            onMapReset={onMapReset}
            onDrawComplete={onDrawComplete}
          ></Map>
        </div>
      </div>
    </Layout>
  );
};

export default connector(RawListingSearch);
